import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import axios from 'axios';
import { Warning, SignOut, Eye, Clock } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImpersonationBanner() {
  const { user, checkAuth } = useAuth();
  const { t } = useI18n();
  const [stopping, setStopping] = useState(false);
  // LOT 537 v62.1quinquies (Daniel 07/06/2026) — Spacer dynamique via
  // ResizeObserver. Avant v62.1quinquies : spacer fixe 58 px (v < quater)
  // puis `sticky top-0` (v62.1quater) qui n'a pas suffi en LIVE (un parent
  // overflow ou un autre header sticky cassait le sticky). Désormais : on
  // mesure la hauteur RÉELLE du bandeau et on la reflète dans un spacer
  // déterministe + une CSS variable `--impersonation-banner-height` exposée
  // sur `documentElement` pour permettre à n'importe quel composant enfant
  // d'utiliser un offset dynamique si nécessaire (eg. sidebar sticky).
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  // Toujours appeler les hooks AVANT le early return conditionnel (règle React).
  useEffect(() => {
    if (!user || !user.impersonation?.active) {
      document.documentElement.style.removeProperty('--impersonation-banner-height');
      return undefined;
    }
    const el = bannerRef.current;
    if (!el) return undefined;
    const apply = (h) => {
      const px = Math.max(0, Math.round(h || 0));
      setBannerHeight(px);
      document.documentElement.style.setProperty('--impersonation-banner-height', `${px}px`);
    };
    // Mesure initiale (sync) + observer pour wrap mobile / changement contenu
    apply(el.getBoundingClientRect().height);
    const ro = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect?.height || 0);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--impersonation-banner-height');
    };
  }, [user]);

  // Only render when impersonation is active
  if (!user || !user.impersonation?.active) return null;

  const { actor, reason, started_at } = user.impersonation;
  const targetName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const targetEmail = user.email;
  const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : '';
  const actorEmail = actor?.email || '';

  const elapsed = started_at ? (() => {
    const mins = Math.round((Date.now() - new Date(started_at).getTime()) / 60000);
    if (mins < 1) return '<1 min';
    if (mins < 60) return `${mins} min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })() : '';

  const handleStop = async () => {
    setStopping(true);
    try {
      // LOT 521 — Backend regenerates a fresh admin JWT + returns user payload.
      // We use that payload directly to avoid a race with /auth/me on Safari ITP.
      const res = await axios.post(`${API}/api/admin/account-admin/impersonate/stop`, {}, { withCredentials: true });
      // Store Bearer-mode fallback token (Lot 494) — use localStorage to match AuthContext
      if (res?.data?.access_token) {
        try { localStorage.setItem('wm_access_token', res.data.access_token); } catch {}
      }
      // Refresh auth state — will now be the original admin (fresh JWT)
      await checkAuth();
      window.location.href = '/admin/account-admin';
    } catch (err) {
      // LOT 537 v63.3ter-bis — Gérer le 400 ("No active impersonation session")
      // qui peut survenir quand le cookie cross-site est bloqué/partitionné
      // (Safari ITP, CHIPS). Dans ce cas, le backend tombe sur le Bearer admin
      // (sans claim imp) et refuse en 400. Fallback : appeler /force-end qui
      // utilise la permission admin (pas le claim imp) pour clore la session
      // d'impersonation côté DB, puis restaurer l'état admin proprement.
      // Anti-régression sécurité : aucun appel à /api/auth/logout, aucun reset
      // de localStorage admin, aucun prompt MFA.
      const status = err?.response?.status;
      if (status === 400) {
        try {
          await axios.post(`${API}/api/admin/account-admin/impersonate/force-end`, {}, { withCredentials: true });
          await checkAuth();
          window.location.href = '/admin/account-admin';
          return;
        } catch (forceErr) {
          // Force-end a échoué : redirect login avec raison claire (pas de logout silencieux)
          try { localStorage.removeItem('wm_access_token'); } catch {}
          window.location.href = '/login?reason=impersonation_stop_failed';
          return;
        }
      }
      setStopping(false);
      // 409/403 = admin compte introuvable ou suspendu → force re-login
      if (status === 409 || status === 403) {
        try { localStorage.removeItem('wm_access_token'); } catch {}
        window.location.href = '/login?reason=impersonation_stop_failed';
      }
    }
  };

  return (
    // LOT 537 v62.1quater (Daniel 07/06/2026) — UX bandeau impersonation :
    // remplacement de `fixed top-0` + spacer fixe `h-[58px]` par `sticky top-0`.
    // Le banner prend désormais sa hauteur réelle et reste collé en haut au
    // scroll, sans masquer le haut du contenu (le contenu démarre naturellement
    // sous le banner). Plus de calcul de hauteur fixe → s'adapte à 1, 2 ou
    // 3 lignes en mobile/tablet. Le bouton « Quitter l'impersonation » et
    // le switcher de profil PP/PM côté investisseur restent pleinement
    // accessibles, peu importe la longueur du contenu du banner.
    <>
      {/* LOT 537 v62.1quinquies — Spacer dynamique mesuré par ResizeObserver
          (couvre exactement la hauteur réelle du bandeau, peu importe le
          wrap mobile/tablet). */}
      <div
        style={{ height: `${bannerHeight}px` }}
        aria-hidden="true"
        data-testid="impersonation-banner-spacer"
        data-lot537-banner-spacer-height={String(bannerHeight)}
      />
      <div
        ref={bannerRef}
        className="fixed top-0 left-0 right-0 z-[9999] bg-[#6B1F2A] text-white shadow-card"
        data-testid="impersonation-banner"
        data-lot537-banner-version="LOT_INVESTOR_DESIGN_KYC_B_impersonation_banner_premium"
        data-lot537-banner-infra="LOT537_v62_1sexies_impersonation_banner_offset_consumers"
        data-lot="investor-design-kyc-b"
        role="alert"
      >
      {/* LOT Investor Design KYC B — Daniel 24/06/2026.
          Bandeau impersonation premium : bordeaux profond (#6B1F2A) plutôt
          que red-600 criard, filet or fin (au lieu de l'ancien stripe pulsé
          jaune/rouge), padding réduit, icônes compactes, bouton Quitter
          ivoire/charcoal sobre. Tracking plafonné selon la DA verrouillée
          (overlines ≤0.08em, CTA ≤0.05em, labels ≤0.06em). La fonctionnalité
          reste strictement identique (claim imp + restauration session admin
          via /stop). */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#C5A572]/70 to-transparent" aria-hidden />

      <div className="px-4 py-1.5 flex items-center justify-between gap-3 max-w-full">
        {/* Left: Warning + context (compact) */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <Eye size={14} weight="duotone" className="text-[#C5A572] flex-shrink-0" />

          <div className="flex items-center gap-2 flex-wrap min-w-0 text-[11.5px] sm:text-xs">
            <span className="font-semibold uppercase tracking-[0.06em] text-[#C5A572] flex-shrink-0">
              {t('impersonation.bannerWarning')}
            </span>
            <span className="text-white/30 hidden sm:inline">·</span>
            <span className="text-white/85 truncate">
              {t('impersonation.actingAs')}{' '}
              <strong className="text-white font-medium">{targetName}</strong>
              <span className="text-white/55 ml-1 hidden md:inline">({targetEmail})</span>
            </span>
            <span className="text-white/30 hidden lg:inline">·</span>
            <span className="text-white/65 hidden lg:inline truncate">
              {t('impersonation.startedBy')} {actorName || actorEmail}
            </span>
            {reason && (
              <>
                <span className="text-white/30 hidden xl:inline">·</span>
                <span className="text-white/55 hidden xl:inline truncate max-w-[200px] italic">
                  {reason}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Right: Timer + Stop button (premium) */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {elapsed && (
            <div className="hidden sm:flex items-center gap-1 text-white/50 text-[10.5px] uppercase tracking-[0.05em]">
              <Clock size={11} />
              <span>{elapsed}</span>
            </div>
          )}
          <button
            onClick={handleStop}
            disabled={stopping}
            className="flex items-center gap-1.5 px-3.5 py-1 bg-ivory text-[#6B1F2A] font-semibold text-[11px] uppercase tracking-[0.05em] hover:bg-white transition-colors disabled:opacity-50"
            data-testid="stop-impersonation-btn"
          >
            <SignOut size={12} weight="bold" />
            {stopping ? t('common.processing') : t('impersonation.stopImpersonation')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
